import React, { useEffect, useState, useCallback } from 'react';

const ACCENT = '#4A7C59';

interface Trip {
  id: string;
  tripDate: string;
  driverName: string | null;
  cherryKgLoaded: string | null;
  cherryKgReceived: string | null;
  status: string;
  notes: string;
  vehicleReg: string | null;
  centreName: string | null;
  collectionCentreId: string | null;
}

interface Vehicle {
  id: string;
  regNumber: string;
  driverName: string | null;
}

interface Centre {
  id: string;
  name: string;
  centreType: string;
}

const STATUS_STYLE: Record<string, string> = {
  planned: 'bg-neutral-100 text-neutral-600',
  in_transit: 'bg-blue-100 text-blue-700',
  arrived: 'bg-green-100 text-green-700',
};

const NEXT_STATUS: Record<string, string> = {
  planned: 'in_transit',
  in_transit: 'arrived',
};

const NEXT_LABEL: Record<string, string> = {
  planned: '→ Dispatch',
  in_transit: '→ Arrived',
};

export const TripList: React.FC = () => {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [centres, setCentres] = useState<Centre[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    tripDate: new Date().toISOString().split('T')[0],
    vehicleId: '',
    driverName: '',
    collectionCentreId: '',
    cherryKgLoaded: '',
    notes: '',
  });

  // kg-received modal when advancing to "arrived"
  const [arrivedModal, setArrivedModal] = useState<{ trip: Trip } | null>(null);
  const [cherryKgReceived, setCherryKgReceived] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tripsRes, vehiclesRes, centresRes] = await Promise.all([
        fetch('/api/production/trips?limit=50', { credentials: 'include' }),
        fetch('/api/production/trips?resource=vehicles', { credentials: 'include' }),
        fetch('/api/production/collection-centres', { credentials: 'include' }),
      ]);
      const [tripsData, vehiclesData, centresData] = await Promise.all([
        tripsRes.json(), vehiclesRes.json(), centresRes.json(),
      ]);
      setTrips(tripsData.trips || []);
      setTotal(tripsData.total || 0);
      setVehicles(vehiclesData.vehicles || []);
      setCentres((centresData.centres || []).filter((c: Centre) => c.centreType === 'station'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const res = await fetch('/api/production/trips', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(form),
    });
    if (res.ok) {
      setShowForm(false);
      setForm({ tripDate: new Date().toISOString().split('T')[0], vehicleId: '', driverName: '', collectionCentreId: '', cherryKgLoaded: '', notes: '' });
      fetchAll();
    }
    setSaving(false);
  };

  const advanceStatus = async (trip: Trip) => {
    const nextStatus = NEXT_STATUS[trip.status];
    if (!nextStatus) return;

    // When advancing to arrived, prompt for received kg
    if (nextStatus === 'arrived') {
      setCherryKgReceived(trip.cherryKgLoaded || '');
      setArrivedModal({ trip });
      return;
    }

    setUpdatingId(trip.id);
    const res = await fetch(`/api/production/trips?id=${trip.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ status: nextStatus }),
    });
    if (res.ok) {
      setTrips(ts => ts.map(t => t.id === trip.id ? { ...t, status: nextStatus } : t));
    }
    setUpdatingId(null);
  };

  const confirmArrived = async () => {
    if (!arrivedModal) return;
    const trip = arrivedModal.trip;
    setUpdatingId(trip.id);
    setArrivedModal(null);
    const res = await fetch(`/api/production/trips?id=${trip.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ status: 'arrived', cherryKgReceived: cherryKgReceived || undefined }),
    });
    if (res.ok) {
      const updated = await res.json();
      setTrips(ts => ts.map(t => t.id === trip.id ? { ...t, ...updated } : t));
    }
    setUpdatingId(null);
  };

  const inputCls = "w-full px-4 py-2.5 border border-[#E8E6E2] rounded-lg text-sm text-[#1A1A1A] bg-white focus:outline-none";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#1A1A1A]">Trips</h2>
          <p className="text-sm text-[#6B6B6B] mt-0.5">{total} total</p>
        </div>
        <button onClick={() => setShowForm(true)} className="px-4 py-2.5 text-sm font-medium text-white rounded-lg" style={{ backgroundColor: ACCENT }}>
          + Log trip
        </button>
      </div>

      {/* New trip modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-[#1A1A1A]">Log trip</h3>
              <button onClick={() => setShowForm(false)} className="text-[#A3A3A3] hover:text-[#1A1A1A]">✕</button>
            </div>

            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-xs text-[#6B6B6B] mb-1">Date <span className="text-red-500">*</span></label>
                <input type="date" value={form.tripDate} onChange={e => setForm(f => ({ ...f, tripDate: e.target.value }))} required className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-[#6B6B6B] mb-1">Collection station <span className="text-red-500">*</span></label>
                <select
                  value={form.collectionCentreId}
                  onChange={e => setForm(f => ({ ...f, collectionCentreId: e.target.value }))}
                  required
                  className={inputCls}
                >
                  <option value="">Select station...</option>
                  {centres.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              {vehicles.length > 0 && (
                <div>
                  <label className="block text-xs text-[#6B6B6B] mb-1">Vehicle</label>
                  <select value={form.vehicleId} onChange={e => {
                    const v = vehicles.find(x => x.id === e.target.value);
                    setForm(f => ({ ...f, vehicleId: e.target.value, driverName: v?.driverName || f.driverName }));
                  }} className={inputCls}>
                    <option value="">Select vehicle...</option>
                    {vehicles.map(v => <option key={v.id} value={v.id}>{v.regNumber}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs text-[#6B6B6B] mb-1">Driver name</label>
                <input type="text" value={form.driverName} onChange={e => setForm(f => ({ ...f, driverName: e.target.value }))} placeholder="Driver name" className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-[#6B6B6B] mb-1">Cherry kg loaded</label>
                <input type="number" value={form.cherryKgLoaded} onChange={e => setForm(f => ({ ...f, cherryKgLoaded: e.target.value }))} placeholder="0.00" step="0.1" className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-[#6B6B6B] mb-1">Notes</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className={`${inputCls} resize-none`} />
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 text-sm text-[#6B6B6B] border border-[#E8E6E2] rounded-lg hover:bg-[#F0EDE8]">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 text-sm font-medium text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: ACCENT }}>
                  {saving ? 'Saving...' : 'Save trip'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Arrived kg modal */}
      {arrivedModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold text-[#1A1A1A] mb-1">Confirm arrival</h3>
            <p className="text-sm text-[#6B6B6B] mb-5">
              Trip from <span className="font-medium text-[#1A1A1A]">{arrivedModal.trip.centreName || '—'}</span> on{' '}
              {new Date(arrivedModal.trip.tripDate).toLocaleDateString('en-KE', { weekday: 'short', day: '2-digit', month: 'short' })}
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-xs text-[#6B6B6B] mb-1.5">Cherry kg received at hub</label>
                <input
                  type="number"
                  value={cherryKgReceived}
                  onChange={e => setCherryKgReceived(e.target.value)}
                  placeholder="0.00"
                  step="0.1"
                  className={inputCls}
                  autoFocus
                />
                {arrivedModal.trip.cherryKgLoaded && (
                  <p className="text-xs text-[#A3A3A3] mt-1">Loaded: {parseFloat(arrivedModal.trip.cherryKgLoaded).toLocaleString()} kg</p>
                )}
              </div>
              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setArrivedModal(null)}
                  className="flex-1 py-2.5 text-sm text-[#6B6B6B] border border-[#E8E6E2] rounded-lg hover:bg-[#F0EDE8]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmArrived}
                  className="flex-1 py-2.5 text-sm font-medium text-white rounded-lg"
                  style={{ backgroundColor: ACCENT }}
                >
                  Confirm arrived
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-sm text-[#6B6B6B]">Loading...</div>
      ) : trips.length === 0 ? (
        <div className="py-16 text-center text-sm text-[#6B6B6B]">No trips logged yet</div>
      ) : (
        <div className="space-y-3">
          {trips.map(trip => (
            <div key={trip.id} className="bg-white border border-[#E8E6E2] rounded-xl p-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-sm font-medium text-[#1A1A1A]">
                    {new Date(trip.tripDate).toLocaleDateString('en-KE', { weekday: 'short', day: '2-digit', month: 'short' })}
                  </span>
                  {trip.vehicleReg && <span className="text-xs text-[#6B6B6B] font-mono">{trip.vehicleReg}</span>}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[trip.status] || 'bg-neutral-100 text-neutral-600'}`}>
                    {trip.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="flex gap-4 text-xs text-[#6B6B6B] flex-wrap">
                  {trip.centreName && <span className="font-medium text-[#4A7C59]">{trip.centreName}</span>}
                  {trip.driverName && <span>{trip.driverName}</span>}
                  {trip.cherryKgLoaded && <span>{parseFloat(trip.cherryKgLoaded).toLocaleString()} kg loaded</span>}
                  {trip.cherryKgReceived && (
                    <span className="text-green-700 font-medium">{parseFloat(trip.cherryKgReceived).toLocaleString()} kg received</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {trip.status === 'arrived' && (
                  <a
                    href={`/production/batches/new?tripId=${trip.id}&centreId=${trip.collectionCentreId || ''}&kgReceived=${trip.cherryKgReceived || ''}`}
                    className="px-3 py-1.5 text-xs font-medium border rounded-lg hover:bg-[#F0EDE8] whitespace-nowrap"
                    style={{ borderColor: ACCENT, color: ACCENT }}
                  >
                    + Create batch
                  </a>
                )}
                {NEXT_STATUS[trip.status] && (
                  <button
                    onClick={() => advanceStatus(trip)}
                    disabled={updatingId === trip.id}
                    className="px-3 py-1.5 text-xs font-medium border border-[#E8E6E2] rounded-lg hover:bg-[#F0EDE8] disabled:opacity-50 whitespace-nowrap"
                  >
                    {updatingId === trip.id ? '...' : NEXT_LABEL[trip.status]}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
